import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import {
  getCommission,
  getCommissionUploadDetail,
  getPayment,
  postCommissionInvoiceUpload,
  getClientsByEmail,
} from "../../../data";

import {
  Link,
  useNavigate,
  useParams,
  useResolvedPath,
} from "react-router-dom";
import { useForm } from "react-hook-form";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useRef, useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { PDFDownloadLink, PDFViewer, pdf } from "@react-pdf/renderer";
import { InvoicePdf } from "@/components";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export function CommissionInvoiceUploadManuelPdfScreen() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [screenLoading, setScreenLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const pathname = useResolvedPath().pathname.split("/")[2];
  const navigate = useNavigate();

  const pdfDowloadLinkRef = useRef();

  const { data, isLoading, error } = useQuery({
    queryKey: ["commissionDetail", pathname],

    queryFn: async () => await getCommissionUploadDetail(pathname),
    retry: 1,
  });

  // Email'e göre client'leri çek
  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ["clientsByEmail", data?.data?.email],
    queryFn: async () => {
      if (data?.data?.email) {
        return await getClientsByEmail(data.data.email);
      }
      return [];
    },
    enabled: !!data?.data?.email,
    retry: 1,
  });

  // Client'ler varsa ve birden fazla ise form'u gizle, yoksa göster
  useEffect(() => {
    if (clientsData && clientsData.length > 0) {
      setShowForm(false);
    } else {
      setShowForm(true);
    }
  }, [clientsData]);

  const mutation = useMutation({
    mutationKey: ["uploadPaymentInvoice"],

    mutationFn: (data) => {
      return postCommissionInvoiceUpload(data);
    },
    onSuccess: (response) => {
      setScreenLoading(false);
      //navigate("/payments");
      if (response.status === 200) {
        queryClient.invalidateQueries(["uploadPaymentInvoice", pathname]);
      }
    },
    onError: (error) => {
      setScreenLoading(false);
      const errorData = error?.response?.data;
      if (errorData?.alreadyUploaded) {
        toast({
          title: "Fatura Zaten Yüklenmiş",
          description: "Bu komisyon için zaten bir fatura yüklenmiş.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Hata",
          description: errorData?.error || "Bir hata oluştu. Lütfen daha sonra tekrar deneyiniz.",
          variant: "destructive",
        });
      }
    },
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  // Client seçildiğinde alanları otomatik doldur
  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setValue("recipientName", client.billingName || "");
    setValue("phoneNumber", client.phoneNumber || "");
    setValue("bankAccountHolder", client.billingName || "");
    setValue("iban", client.IBAN || "");
    setValue("swift", client.SWIFT || "");
    setShowForm(true);
    setIsDialogOpen(false);
  };

  // Yeni bilgilerle fatura oluştur butonuna tıklandığında
  const handleCreateWithNewInfo = () => {
    setSelectedClient(null);
    setValue("recipientName", "");
    setValue("phoneNumber", "");
    setValue("bankAccountHolder", "");
    setValue("iban", "");
    setValue("swift", "");
    setShowForm(true);
    setIsDialogOpen(false);
  };

  const onSubmit = async (values) => {
    setScreenLoading(true);

    // Fatura tarihini ayarla
    setValue(
      "invoiceDate", 
      new Intl.DateTimeFormat("tr-TR", {
        day: "numeric",
        month: "numeric",
        year: "numeric",
      }).format(new Date())
    );

    // Tüm değerleri trim ederek al
    const recipientName = (getValues("recipientName") || "").trim();
    const bankAccountHolder = (getValues("bankAccountHolder") || "").trim();
    const swift = (getValues("swift") || "").trim();
    const iban = (getValues("iban") || "").trim();
    const phoneNumber = (getValues("phoneNumber") || "").trim();
    const invoiceDate = (getValues("invoiceDate") || "").trim();

    var pdfData = {
      invoiceId: data.data.id,
      email: data.data.email?.trim() || "",
      lucaId: getLucaId(bankAccountHolder) || "",
      recipientName: recipientName,
      finalAmount: data.data.finalAmount,
      bankAccountHolder: bankAccountHolder,
      swift: swift,
      iban: iban,
      invoiceDate: invoiceDate,
      phoneNumber: phoneNumber,
    };

    if (
      !pdfData.recipientName ||
      !pdfData.bankAccountHolder ||
      !pdfData.iban ||
      !pdfData.phoneNumber
    ) {
      setScreenLoading(false);
      toast({
        title: "Lütfen tüm alanları doldurunuz",
        message: "",
        type: "error",
      });
      return;
    }

    const formData = new FormData();
    Object.entries(pdfData).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append("fileName", `${(data.data.influencerName || "").trim()}-${data.data.id}`);

    try {
      var blob = await pdf(InvoicePdf({ data: pdfData })).toBlob();
      formData.append("file", blob, "invoice.pdf");

      mutation.mutate({
        path: pathname,
        data: formData,
        invoiceId: data.data.payment.invoiceId,
      });
    } catch (error) {
      setScreenLoading(false);
      toast({
        title: "Fatura oluşturulurken bir hata oluştu",
        message: "Lütfen daha sonra tekrar deneyiniz",
        type: "error",
      });
    }
  };
  const getLucaId = (bankAccountHolder) => {
    if (data.data.client) {
      if (data.data.client.lucaAccountName == bankAccountHolder) {
        return data.data.client.lucaId;
      }
    }
    return "";
  };

  if (isLoading || screenLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="w-[350px] ">
          <CardHeader>
            <CardTitle>Upload Invoice</CardTitle>
            <CardDescription>
              Please wait while we are processing your request
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center">
              <div
                className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              >
                <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                  Loading...
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="w-[350px] ">
          <CardHeader>
            <CardTitle>Error</CardTitle>
            <CardDescription>
              Something went wrong. Please try again later. If the problem
              persists, please contact our support team.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-screen">
      {data.data.status == "Invoice Uploaded" ? (
        <div className="flex items-center justify-center h-screen">
          <Card className="w-[350px] ">
            <CardHeader>
              <CardTitle>Success</CardTitle>
              <CardDescription>
                Your invoice has been sent successfully. You can close this page
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Card className="w-[350px] ">
            <CardHeader>
              <CardTitle>Fatura İşlemleri</CardTitle>
              <CardDescription>
                Komisyon faturası olustumak ve onaylamak için lütfen odeme
                bilgilerinizi giriniz. Banka bilgilerini girdikten sonra lutfen
                onaylayiniz.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid w-full items-center gap-4">
                <div className="flex flex-col space-y-1.5">
                  <Label htmlFor="name">Email</Label>
                  <h1 id="name">{data.data.email}</h1>
                </div>
                {!selectedClient && (
                  <div className="flex flex-col space-y-1.5">
                    <Label htmlFor="name">Fatura Tutarı</Label>
                    <h1 id="name">{data.data.finalAmount} $</h1>
                  </div>
                )}

                {/* Client seçimi dialog - sadece client'ler varsa göster */}
                {clientsData && clientsData.length > 0 && !showForm && (
                  <div className="flex flex-col space-y-1.5">
                    <Label>Kayıtlı Fatura Bilgisi Seçin</Label>
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="default" className="w-full">
                          Kayıtlı Bilgi Seç ({clientsData.length} bilgi bulundu)
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Bilgi Seçin</DialogTitle>
                          <DialogDescription>
                            Bu email adresi için kayıtlı bilgiler aşağıda
                            listelenmektedir. Birini seçin veya yeni bilgilerle
                            fatura oluşturun.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                          {clientsData.map((client) => (
                            <Card
                              key={client.id}
                              className="cursor-pointer hover:bg-muted transition-colors border-2 hover:border-blue-300"
                              onClick={() => handleClientSelect(client)}
                            >
                              <CardContent className="p-4 relative">
                                <div className="grid grid-cols-2 gap-4 pb-8">
                                  <div>
                                    <Label className="text-sm font-medium text-muted-foreground">
                                      Ad Soyad
                                    </Label>
                                    <p className="text-sm">
                                      {client.billingName || "-"}
                                    </p>
                                  </div>
                                  <div>
                                    <Label className="text-sm font-medium text-muted-foreground">
                                      Telefon
                                    </Label>
                                    <p className="text-sm">
                                      {client.phoneNumber || "-"}
                                    </p>
                                  </div>
                                  <div>
                                    <Label className="text-sm font-medium text-muted-foreground">
                                      Alıcı Adı Soyadı
                                    </Label>
                                    <p className="text-sm">
                                      {client.billingName || "-"}
                                    </p>
                                  </div>
                                  <div>
                                    <Label className="text-sm font-medium text-muted-foreground">
                                      IBAN/Kart No
                                    </Label>
                                    <p className="text-sm">
                                      {client.IBAN || "-"}
                                    </p>
                                  </div>
                                  <div>
                                    <Label className="text-sm font-medium text-muted-foreground">
                                      SWIFT
                                    </Label>
                                    <p className="text-sm">
                                      {client.SWIFT || "-"}
                                    </p>
                                  </div>
                                </div>
                                <div className="absolute bottom-3 right-3">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClientSelect(client);
                                    }}
                                    className="text-xs"
                                  >
                                    Bu Bilgileri Seç
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                        <div className="flex justify-between pt-4 border-t">
                          <Button
                            variant="outline"
                            onClick={() => setIsDialogOpen(false)}
                          >
                            İptal
                          </Button>
                          <Button onClick={handleCreateWithNewInfo}>
                            Yeni Bilgilerle Fatura Oluştur
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}

                {/* Form alanları - sadece showForm true ise göster */}
                {showForm && (
                  <>
                    {selectedClient && (
                      <div className="flex flex-col space-y-1.5 p-3 bg-green-50 rounded border border-green-200">
                        <div className="flex items-center justify-between">
                          <Label className="text-green-700">
                            Seçilen Bilgiler
                          </Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setIsDialogOpen(true)}
                            className="text-xs"
                          >
                            Bilgileri Değiştir
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="font-medium text-green-700">
                              Ad Soyad:
                            </span>
                            <p className="text-green-600">
                              {selectedClient.billingName || "-"}
                            </p>
                          </div>
                          <div>
                            <span className="font-medium text-green-700">
                              Telefon:
                            </span>
                            <p className="text-green-600">
                              {selectedClient.phoneNumber || "-"}
                            </p>
                          </div>
                          <div>
                            <span className="font-medium text-green-700">
                              Alıcı Adı:
                            </span>
                            <p className="text-green-600">
                              {selectedClient.billingName || "-"}
                            </p>
                          </div>
                          <div>
                            <span className="font-medium text-green-700">
                              IBAN:
                            </span>
                            <p className="text-green-600">
                              {selectedClient.IBAN || "-"}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Dialog - hem ilk seçim hem de değiştirme için */}
                    {clientsData &&
                      clientsData.length > 0 &&
                      selectedClient && (
                        <Dialog
                          open={isDialogOpen}
                          onOpenChange={setIsDialogOpen}
                        >
                          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>Bilgileri Değiştir</DialogTitle>
                              <DialogDescription>
                                Farklı bir bilgi seçin veya yeni bilgilerle
                                fatura oluşturun.
                              </DialogDescription>
                            </DialogHeader>
                            <div className="grid gap-4 py-4">
                              {clientsData.map((client) => (
                                <Card
                                  key={client.id}
                                  className={`cursor-pointer transition-colors border-2 hover:border-blue-300 ${
                                    selectedClient?.id === client.id
                                      ? "bg-blue-50 border-blue-400"
                                      : "hover:bg-muted"
                                  }`}
                                  onClick={() => handleClientSelect(client)}
                                >
                                  <CardContent className="p-4 relative">
                                    <div className="grid grid-cols-2 gap-4 pb-8">
                                      <div>
                                        <Label className="text-sm font-medium text-muted-foreground">
                                          Ad Soyad
                                        </Label>
                                        <p className="text-sm">
                                          {client.billingName || "-"}
                                        </p>
                                      </div>
                                      <div>
                                        <Label className="text-sm font-medium text-muted-foreground">
                                          Telefon
                                        </Label>
                                        <p className="text-sm">
                                          {client.phoneNumber || "-"}
                                        </p>
                                      </div>
                                      <div>
                                        <Label className="text-sm font-medium text-muted-foreground">
                                          Alıcı Adı Soyadı
                                        </Label>
                                        <p className="text-sm">
                                          {client.billingName || "-"}
                                        </p>
                                      </div>
                                      <div>
                                        <Label className="text-sm font-medium text-muted-foreground">
                                          IBAN/Kart No
                                        </Label>
                                        <p className="text-sm">
                                          {client.IBAN || "-"}
                                        </p>
                                      </div>
                                      <div>
                                        <Label className="text-sm font-medium text-muted-foreground">
                                          SWIFT
                                        </Label>
                                        <p className="text-sm">
                                          {client.SWIFT || "-"}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="absolute bottom-3 right-3">
                                      <Button
                                        size="sm"
                                        variant={
                                          selectedClient?.id === client.id
                                            ? "default"
                                            : "outline"
                                        }
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleClientSelect(client);
                                        }}
                                        className="text-xs"
                                      >
                                        {selectedClient?.id === client.id
                                          ? "Seçildi"
                                          : "Bu Bilgileri Seç"}
                                      </Button>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                            <div className="flex justify-between pt-4 border-t">
                              <Button
                                variant="outline"
                                onClick={() => setIsDialogOpen(false)}
                              >
                                İptal
                              </Button>
                              <Button onClick={handleCreateWithNewInfo}>
                                Yeni Bilgilerle Fatura Oluştur
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}

                    <div className="flex flex-col space-y-1.5">
                      <Label htmlFor="framework">Ad-Soyadı</Label>
                      {!selectedClient && (
                        <span className="text-red-500 text-sm">
                          Lütfen sadece Türkçe karakterler kullanın.
                        </span>
                      )}
                      <Input
                        id="recipientName"
                        type="text"
                        value={watch("recipientName") || ""}
                        className="disabled:opacity-100 disabled:text-foreground disabled:bg-muted"
                        onChange={(e) => {
                          setValue("recipientName", e.target.value);
                        }}
                      />
                    </div>
                    <div className="flex flex-col space-y-1.5">
                      <Label htmlFor="framework">Telefon Numarası</Label>

                      <Input
                        id="phoneNumber"
                        type="text"
                        value={watch("phoneNumber") || ""}
                        className="disabled:opacity-100 disabled:text-foreground disabled:bg-muted"
                        onChange={(e) => {
                          setValue("phoneNumber", e.target.value);
                        }}
                      />
                    </div>
                    <div className="flex flex-col space-y-1.5">
                      <Label htmlFor="framework">Fatura Tarihi </Label>
                      <h1 id="name">
                        {new Intl.DateTimeFormat("tr-TR", {
                          day: "numeric",
                          month: "numeric",
                          year: "numeric",
                        }).format(new Date())}
                      </h1>
                    </div>

                    <div className="flex flex-col space-y-1.5">
                      <Label htmlFor="framework">Alıcı Adı Soyadı</Label>
                      {!selectedClient && (
                        <span className="text-red-500 text-sm">
                          Lütfen sadece Türkçe karakterler kullanın. Lütfen bu
                          alana BANKA adı yazmayınız.
                        </span>
                      )}
                      <Input
                        id="bankAccountHolder"
                        type="text"
                        value={watch("bankAccountHolder") || ""}
                        className="disabled:opacity-100 disabled:text-foreground disabled:bg-muted"
                        onChange={(e) => {
                          setValue("bankAccountHolder", e.target.value);
                        }}
                      />
                    </div>
                    <div className="flex flex-col space-y-1.5">
                      <Label htmlFor="framework">IBAN veya Kart No</Label>

                      <Input
                        id="iban"
                        type="text"
                        value={watch("iban") || ""}
                        className="disabled:opacity-100 disabled:text-foreground disabled:bg-muted"
                        onChange={(e) => {
                          setValue("iban", e.target.value);
                        }}
                      />
                    </div>
                    <div className="flex flex-col space-y-1.5">
                      <Label htmlFor="framework">
                        SWIFT (Yoksa boş geçilebilir)
                      </Label>

                      <Input
                        id="swift"
                        type="text"
                        value={watch("swift") || ""}
                        className="disabled:opacity-100 disabled:text-foreground disabled:bg-muted"
                        onChange={(e) => {
                          setValue("swift", e.target.value);
                        }}
                      />
                    </div>
                    <div className="flex flex-col space-y-1.5">
                      <Label htmlFor="name">Fatura Tutarı</Label>
                      <h1 id="name">{data.data.finalAmount} $</h1>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
            {showForm && (
              <CardFooter className="flex justify-between">
                <div></div>
                <Button type="submit">Fatura Oluştur ve Gönder</Button>
              </CardFooter>
            )}
          </Card>
        </form>
      )}
    </div>
  );
}
